import { logDebug, logError } from '@/utils/logger';
const LOCAL_STORAGE_CACHE_KEY = 'iconUrlCache';
interface IconUrlCache {
  [symbol: string]: string;
}
let inMemoryCache: IconUrlCache | null = null;
const initializeInMemoryCache = (): IconUrlCache => {
  if (inMemoryCache === null) {
    inMemoryCache = readFromLocalStorage();
  }
  return inMemoryCache;
};
const readFromLocalStorage = (): IconUrlCache => {
  if (typeof window === 'undefined') {
    return {};
  }
  try {
    const cachedData = localStorage.getItem(LOCAL_STORAGE_CACHE_KEY);
    if (cachedData) {
      return JSON.parse(cachedData) as IconUrlCache;
    }
  } catch (error) {
    logError(error, 'IconCacheService.readFromLocalStorage', {
      message: 'Failed to read or parse icon cache from localStorage',
    });
  }
  return {};
};
const writeToLocalStorage = (cache: IconUrlCache): void => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    localStorage.setItem(LOCAL_STORAGE_CACHE_KEY, JSON.stringify(cache));
  } catch (error) {
    logError(error, 'IconCacheService.writeToLocalStorage', {
      message: 'Failed to write icon cache to localStorage',
    });
  }
};
export const getCachedIconUrl = (symbolLowercase: string): string | null => {
  if (!symbolLowercase) return null;
  const cache = initializeInMemoryCache();
  const url = cache[symbolLowercase] || null;
  if (url) {
    logDebug(
      `In-memory cache hit for icon: ${symbolLowercase}`,
      'IconCacheService.get',
    );
  }
  return url;
};
export const setCachedIconUrl = (
  symbolLowercase: string,
  url: string,
): void => {
  if (!symbolLowercase || !url) return;
  try {
    const cache = initializeInMemoryCache();
    if (cache[symbolLowercase] !== url) {
      cache[symbolLowercase] = url;
      writeToLocalStorage(cache);
      logDebug(
        `Cached icon URL for ${symbolLowercase} in both caches`,
        'IconCacheService.set',
        { url },
      );
    }
  } catch (error) {
    logError(error, 'IconCacheService.setCachedIconUrl', {
      message: 'Failed to update icon cache',
      symbol: symbolLowercase,
      url,
    });
  }
};
